import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FileCheck } from "lucide-react";
import apiClient from "@/api/client";
import { Badge } from "@/components/ui/Badge";
import { PageLoader } from "@/components/ui/Spinner";
import { MobileSortBar, SortableTh, TableSearchInput, useTableSort } from "@/components/ui/TableSort";
import { naturalCompare } from "@/utils/naturalCompare";

interface Protocol {
  id: string;
  protocol_number: string;
  order_number: string;
  is_final: boolean;
  is_withdrawn: boolean;
  without_amounts?: boolean;
  accepted_by_contractor: boolean | null;
  created_at: string;
}

// Own POCK request still awaiting GK approval — no protocol exists yet, so it
// would otherwise be invisible here until (and unless) it gets approved.
interface PendingPockRequest {
  id: string;
  order: string;
  order_number: string;
  is_withdrawn: boolean;
  created_at: string;
}

type Row =
  | ({ kind: "protocol" } & Protocol)
  | ({ kind: "request" } & PendingPockRequest);

const REQUEST_ROW_LABEL = "Prośba o POCK";

function rowNumberLabel(r: Row): string {
  return r.kind === "protocol" ? r.protocol_number : REQUEST_ROW_LABEL;
}

// A pending request has no protocol page yet — it lives on the order.
function rowHref(r: Row): string {
  return r.kind === "protocol" ? `/contractor/protocols/${r.id}` : `/contractor/orders/${r.order}`;
}

function isWithdrawn(r: Row): boolean {
  return r.kind === "protocol" && r.is_withdrawn;
}

type SortField = "protocol_number" | "order_number" | "type" | "status" | "created_at";

const SORT_LABELS: Record<SortField, string> = {
  protocol_number: "Nr protokołu",
  order_number: "Zamówienie",
  type: "Typ",
  status: "Status",
  created_at: "Data",
};

// Czekające na zatwierdzenie przez GK, potem oczekujące na naszą odpowiedź,
// wycofane na końcu — jak w kolumnie „Status".
function statusRank(r: Row): number {
  if (r.kind === "request") return 0;
  if (r.is_withdrawn) return 4;
  if (r.accepted_by_contractor === null) return 1;
  return r.accepted_by_contractor ? 2 : 3;
}

// Requests have no type yet, so they sort ahead of both protocol types.
function typeRank(r: Row): number {
  if (r.kind === "request") return -1;
  return Number(r.is_final);
}

function compareRows(a: Row, b: Row, field: SortField): number {
  switch (field) {
    case "protocol_number":
      return naturalCompare(rowNumberLabel(a), rowNumberLabel(b));
    case "order_number":
      return naturalCompare(a.order_number, b.order_number);
    case "type":
      return typeRank(a) - typeRank(b);
    case "status":
      return statusRank(a) - statusRank(b);
    case "created_at":
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
  }
}

export function ContractorProtocolsPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["contractor-protocols"],
    queryFn: () =>
      apiClient.get<{ data: Protocol[] }>("/protocols/").then((r) => r.data.data),
  });

  // Own requests still awaiting GK approval. A withdrawn request keeps
  // status=pending server-side — drop it here.
  const { data: pendingRequests, isLoading: isLoadingRequests } = useQuery({
    queryKey: ["contractor-protocols", "pending-pock-requests"],
    queryFn: () =>
      apiClient
        .get<{ data: PendingPockRequest[] }>("/protocols/pock-requests/", {
          params: { status: "pending" },
        })
        .then((r) => r.data.data.filter((pr) => !pr.is_withdrawn)),
  });

  const rows = useMemo<Row[]>(
    () => [
      ...(pendingRequests ?? []).map((r) => ({ kind: "request" as const, ...r })),
      ...(data ?? []).map((p) => ({ kind: "protocol" as const, ...p })),
    ],
    [data, pendingRequests],
  );
  const { sortField, sortDir, handleSort, setField, toggleDir } = useTableSort<SortField>();
  const [search, setSearch] = useState("");

  // Default: awaiting GK approval first, then unreviewed, then most recent.
  // A clicked header takes over.
  const sorted = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = q
      ? rows.filter(
          (r) =>
            rowNumberLabel(r).toLowerCase().includes(q) ||
            r.order_number.toLowerCase().includes(q),
        )
      : rows;
    if (sortField) {
      const dir = sortDir === "asc" ? 1 : -1;
      return [...list].sort((a, b) => dir * compareRows(a, b, sortField));
    }
    return [...list].sort((a, b) => {
      if (a.kind !== b.kind) return a.kind === "request" ? -1 : 1;
      if (a.kind === "request" || b.kind === "request") {
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      }
      if (a.is_withdrawn !== b.is_withdrawn) return a.is_withdrawn ? 1 : -1;
      if ((a.accepted_by_contractor === null) !== (b.accepted_by_contractor === null)) {
        return a.accepted_by_contractor === null ? -1 : 1;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });
  }, [rows, search, sortField, sortDir]);

  const pendingCount = rows.filter(
    (r) => r.kind === "protocol" && !r.is_withdrawn && r.accepted_by_contractor === null
  ).length;
  const awaitingApprovalCount = rows.filter((r) => r.kind === "request").length;

  if (isLoading || isLoadingRequests) return <PageLoader />;

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-3 mb-6">
        <FileCheck size={22} className="text-[var(--color-primary)]" />
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Protokoły POCK</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {rows.length} {rows.length === 1 ? "pozycja" : "pozycji"}
            {pendingCount > 0 && (
              <span className="ml-2 text-amber-600 font-medium">· {pendingCount} oczekuje na odpowiedź</span>
            )}
            {awaitingApprovalCount > 0 && (
              <span className="ml-2 text-amber-600 font-medium">
                · {awaitingApprovalCount} czeka na zatwierdzenie przez GK
              </span>
            )}
          </p>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-12 text-center">
          <FileCheck size={32} className="text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400">Brak protokołów POCK.</p>
        </div>
      ) : (
        <>
          <TableSearchInput
            value={search}
            onChange={setSearch}
            placeholder="Szukaj: nr protokołu / zamówienia…"
          />
          {/* Mobile sort bar — the table headers do this job on desktop */}
          <MobileSortBar
            id="contractor-protocols-sort"
            labels={SORT_LABELS}
            sortField={sortField}
            sortDir={sortDir}
            onFieldChange={setField}
            onDirToggle={toggleDir}
          />
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800">
            {sorted.map((r) => {
              const withdrawn = isWithdrawn(r);
              const highlight = r.kind === "request" || (!withdrawn && r.accepted_by_contractor === null);
              return (
                <div
                  key={r.id}
                  className={`px-4 py-3 ${withdrawn ? "opacity-50" : highlight ? "bg-amber-50 dark:bg-amber-900/10" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <Link
                        to={rowHref(r)}
                        className={`font-medium hover:underline block truncate ${withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-[var(--color-primary)]"}`}
                      >
                        {rowNumberLabel(r)}
                      </Link>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{r.order_number}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                        {new Date(r.created_at).toLocaleDateString("pl")}
                      </p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      {r.kind === "request" ? (
                        <Badge color="yellow">Oczekuje na zatwierdzenie</Badge>
                      ) : r.is_withdrawn ? (
                        <Badge color="gray">Wycofany</Badge>
                      ) : (
                        <>
                          <Badge color={r.is_final ? "purple" : "blue"}>
                            {r.is_final ? "Końcowy" : "Częściowy"}
                          </Badge>
                          {r.without_amounts && <Badge color="orange">Bez kwot</Badge>}
                          {r.accepted_by_contractor === null ? (
                            <Badge color="yellow">Oczekuje</Badge>
                          ) : r.accepted_by_contractor ? (
                            <Badge color="green">Zaakceptowany</Badge>
                          ) : (
                            <Badge color="red">Odrzucony</Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {sorted.length === 0 && (
              <p className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm">
                Brak protokołów pasujących do wyszukiwania
              </p>
            )}
          </div>
          {/* Desktop table */}
          <div className="hidden sm:block bg-white dark:bg-gray-800 overflow-x-auto">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                <tr>
                  <SortableTh field="protocol_number" label="Nr protokołu" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="order_number" label="Zamówienie" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="type" label="Typ" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="status" label="Status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                  <SortableTh field="created_at" label="Data" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sorted.map((r) => {
                  const withdrawn = isWithdrawn(r);
                  const highlight = r.kind === "request" || (!withdrawn && r.accepted_by_contractor === null);
                  return (
                    <tr
                      key={r.id}
                      className={
                        withdrawn
                          ? "opacity-50"
                          : highlight
                          ? "bg-amber-50 dark:bg-amber-900/10 hover:bg-amber-100 dark:hover:bg-amber-900/20"
                          : "hover:bg-gray-50 dark:hover:bg-gray-700/50"
                      }
                    >
                      <td className="px-4 py-3 font-medium">
                        <Link
                          to={rowHref(r)}
                          className={`hover:underline ${withdrawn ? "line-through text-gray-400 dark:text-gray-500" : "text-[var(--color-primary)]"}`}
                        >
                          {rowNumberLabel(r)}
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400">{r.order_number}</td>
                      <td className="px-4 py-3">
                        {r.kind === "request" ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        ) : r.is_withdrawn ? (
                          <Badge color="gray">Wycofany</Badge>
                        ) : (
                          <>
                            <Badge color={r.is_final ? "purple" : "blue"}>
                              {r.is_final ? "Końcowy" : "Częściowy"}
                            </Badge>
                            {r.without_amounts && <Badge color="orange">Bez kwot</Badge>}
                          </>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.kind === "request" ? (
                          <Badge color="yellow">Oczekuje na zatwierdzenie</Badge>
                        ) : r.is_withdrawn ? (
                          <span className="text-xs text-gray-400 dark:text-gray-500">—</span>
                        ) : r.accepted_by_contractor === null ? (
                          <Badge color="yellow">Oczekuje na odpowiedź</Badge>
                        ) : r.accepted_by_contractor ? (
                          <Badge color="green">Zaakceptowany</Badge>
                        ) : (
                          <Badge color="red">Odrzucony</Badge>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {new Date(r.created_at).toLocaleDateString("pl")}
                      </td>
                    </tr>
                  );
                })}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400 dark:text-gray-500">
                      Brak protokołów pasujących do wyszukiwania
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
